/**
 * Per-modal chunk loaders + component registry.
 *
 * ensureModalChunk() loads the module and caches its default export so
 * renderModalContent can mount the real component synchronously — avoiding
 * next/dynamic's empty loading frame (the ShareIcon first-open flicker).
 */

import type { ComponentType } from "react";
import { ModalTypes } from "@/types/modal";

type AnyComponent = ComponentType<any>;
type ChunkLoader = () => Promise<unknown>;

const loaders: Partial<Record<ModalTypes, ChunkLoader>> = {
  [ModalTypes.VideoPopup]: () =>
    import("@organisms/VideoPopup/VideoPopup"),
  [ModalTypes.LoginModal]: () => import("@organisms/LoginModal"),
  [ModalTypes.LoginRegister]: () =>
    import("@/components/organisms/modal/ConnectModalMba/ConnectModalMba"),
  [ModalTypes.WebinarModal]: () =>
    import("@/components/organisms/modal/WebinarModal/WebinarModal"),
  [ModalTypes.ConnectModalLP]: () =>
    import("@/components/organisms/modal/ConnectModalLP/ConnectModalLP"),
  [ModalTypes.ConnectModal]: () =>
    import("@organisms/modal/ConnectModal/ConnectModal"),
  [ModalTypes.CallUsModal]: () =>
    import("@organisms/modal/ConnectModal/ConnectModal"),
  [ModalTypes.ConnectModalV2]: () =>
    import("@/components/organisms/modal/ConnectModalV2/ConnectModalV2"),

  [ModalTypes.ConnectModalPartner]: () =>
    import("@/components/organisms/modal/ConnectModalPartner/ConnectModalPartner"),
  
  [ModalTypes.ConnectModalMobileNumber]: () =>
    import(
      "@/components/organisms/modal/ConnectModalMobileNumber/ConnectModalMobileNumber"
    ),
  [ModalTypes.BrandModal]: () =>
    import("@/components/organisms/modal/BrandModal/BrandModal"),
  [ModalTypes.ConnectModalMba]: () =>
    import("@/components/organisms/modal/ConnectModalMba/ConnectModalMba"),
  [ModalTypes.ConnectModalB2I]: () =>
    import("@/components/organisms/modal/ConnectModalB2I/ConnectModalB2I"),
  [ModalTypes.ConnectModalAONSAT]: () =>
    import("@/components/organisms/modal/ConnectModalIOSAT/ConnectModalIOSAT"),
  [ModalTypes.scholarshipRFI]: () =>
    import("@/components/atoms/FeeScholorshipRFI/FeeScholorshipRFI"),
  [ModalTypes.ConnectModalCalender]: () =>
    import("@organisms/modal/ConnectModalCalender/ConnectModalCalender"),
  [ModalTypes.ConnectModalVideoCounselling]: () =>
    import(
      "@organisms/modal/ConnectModalVideoCounselling/ConnectModalVideoCounselling"
    ),
  [ModalTypes.ConnectModalVideoCounsellingLandingPage]: () =>
    import(
      "@/components/organisms/modal/ConnectModalVideoCounsellingLP/ConnectModalVideoCounsellingLandingPage"
    ),
  [ModalTypes.LandingPageConnectModalTempleteOne]: () =>
    import(
      "@organisms/modal/ConnectModalFormLPTempleteOne/LandingPageConnectModalTempleteOne"
    ),
  [ModalTypes.ConnectModalFormSponsoredCourses]: () =>
    import(
      "@organisms/modal/ConnectModalSponsoredCourses/ConnectModalSponsoredCourses"
    ),
  [ModalTypes.DownloadBrochure]: () =>
    import("@organisms/modal/DownloadBrochure"),
  [ModalTypes.DownloadBrochureLP]: () =>
    import("@organisms/modal/DownloadBrochureLP/DownloadBrochureLP"),
  [ModalTypes.CallUsModal_V2]: () =>
    import("@organisms/modal/CallUsModalV2"),
  [ModalTypes.ConnectModal30Sec]: () =>
    Promise.all([
      import("@/components/organisms/modal/ConnectModal30Sec/ConnectModal30Sec"),
      import("@/components/organisms/modal/ConnectModalIOSAT/ConnectModalIOSAT"),
    ]),
  [ModalTypes.CallingSticky_Modal]: () =>
    import("@organisms/modal/CallingSticky_Modal"),
  [ModalTypes.emiModal]: () =>
    import("@organisms/FeesSection/EmiModalContent"),
  [ModalTypes.RegistrationForm]: () =>
    Promise.all([
      import("@organisms/MediaFormSection/MediaFormSection"),
      import("@organisms/modal/RegistrationForm/RegistrationForm"),
    ]),
  [ModalTypes.ShareIcon]: () =>
    import("@/components/organisms/ShareBanner/ShareBanner"),
  [ModalTypes.TestimonialModal]: () =>
    import("@organisms/modal/TestimonialModal/TestimonialModal"),
  [ModalTypes.ConnectModalScholarship]: () =>
    import(
      "@/components/organisms/modal/ConnectModalScholarshipTest/ConnectModalScholarshipTest"
    ),
  [ModalTypes.ConnectModalFAQ]: () =>
    import("@/components/organisms/modal/ConnectModalFAQ/ConnectModalFAQ"),
  [ModalTypes.ProfessorModal]: () =>
    import("@/components/atoms/Professor/ProfessorModal/ProfessorModal"),
};

/** Named slots for multi-module modal types. */
export type ModalComponentKey =
  | ModalTypes
  | "ConnectModal30SecBody"
  | "ConnectModalIOSAT"
  | "MediaFormSection"
  | "RegistrationFormBody";

const components = new Map<ModalComponentKey, AnyComponent>();
const inflight = new Map<ModalTypes, Promise<unknown>>();
const ready = new Set<ModalTypes>();

function asDefault(mod: unknown): AnyComponent {
  return (mod as { default: AnyComponent }).default;
}

function cacheModules(modalType: ModalTypes, mod: unknown): void {
  switch (modalType) {
    case ModalTypes.ConnectModal30Sec: {
      const [thirty, iosat] = mod as [unknown, unknown];
      components.set("ConnectModal30SecBody", asDefault(thirty));
      components.set("ConnectModalIOSAT", asDefault(iosat));
      // AONSAT open path also uses IOSAT alone.
      components.set(ModalTypes.ConnectModalAONSAT, asDefault(iosat));
      ready.add(ModalTypes.ConnectModalAONSAT);
      break;
    }
    case ModalTypes.RegistrationForm: {
      const [media, form] = mod as [unknown, unknown];
      components.set("MediaFormSection", asDefault(media));
      components.set("RegistrationFormBody", asDefault(form));
      break;
    }
    case ModalTypes.CallUsModal:
    case ModalTypes.ConnectModal:
      components.set(ModalTypes.ConnectModal, asDefault(mod));
      components.set(ModalTypes.CallUsModal, asDefault(mod));
      ready.add(ModalTypes.ConnectModal);
      ready.add(ModalTypes.CallUsModal);
      break;
    case ModalTypes.LoginRegister:
    case ModalTypes.ConnectModalMba:
      components.set(ModalTypes.ConnectModalMba, asDefault(mod));
      components.set(ModalTypes.LoginRegister, asDefault(mod));
      ready.add(ModalTypes.ConnectModalMba);
      ready.add(ModalTypes.LoginRegister);
      break;
    case ModalTypes.ConnectModalAONSAT:
      components.set(ModalTypes.ConnectModalAONSAT, asDefault(mod));
      components.set("ConnectModalIOSAT", asDefault(mod));
      break;
    default:
      components.set(modalType, asDefault(mod));
  }
}

export function getModalComponent(
  key: ModalComponentKey,
): AnyComponent | undefined {
  return components.get(key);
}

export function isModalChunkReady(modalType?: ModalTypes | null): boolean {
  if (!modalType) return true;
  if (!loaders[modalType]) return true;
  if (!ready.has(modalType)) return false;

  if (modalType === ModalTypes.ConnectModal30Sec) {
    return (
      components.has("ConnectModal30SecBody") &&
      components.has("ConnectModalIOSAT")
    );
  }
  if (modalType === ModalTypes.RegistrationForm) {
    return (
      components.has("MediaFormSection") &&
      components.has("RegistrationFormBody")
    );
  }
  if (
    modalType === ModalTypes.CallUsModal ||
    modalType === ModalTypes.ConnectModal
  ) {
    return components.has(ModalTypes.ConnectModal);
  }
  if (
    modalType === ModalTypes.LoginRegister ||
    modalType === ModalTypes.ConnectModalMba
  ) {
    return components.has(ModalTypes.ConnectModalMba);
  }
  if (modalType === ModalTypes.ConnectModalAONSAT) {
    return (
      components.has(ModalTypes.ConnectModalAONSAT) ||
      components.has("ConnectModalIOSAT")
    );
  }

  return components.has(modalType);
}

/** Load (or reuse) the chunk for a modal type. Safe to call repeatedly. */
export function ensureModalChunk(
  modalType?: ModalTypes | null,
): Promise<unknown> {
  if (!modalType) return Promise.resolve();
  const loader = loaders[modalType];
  if (!loader) return Promise.resolve();
  if (isModalChunkReady(modalType)) return Promise.resolve();

  let pending = inflight.get(modalType);
  if (!pending) {
    pending = loader()
      .then((mod) => {
        cacheModules(modalType, mod);
        ready.add(modalType);
        inflight.delete(modalType);
        return mod;
      })
      .catch((err) => {
        inflight.delete(modalType);
        throw err;
      });
    inflight.set(modalType, pending);
  }
  return pending;
}

/**
 * High-traffic + share (program hero) — warmed on idle/intent so first open
 * paints from the component registry with no dynamic() loading frame.
 */
export const HOT_MODAL_TYPES: ModalTypes[] = [
  ModalTypes.ConnectModal,
  ModalTypes.ConnectModalV2,
  ModalTypes.ConnectModalMobileNumber,
  ModalTypes.ConnectModalMba,
  ModalTypes.DownloadBrochure,
  ModalTypes.DownloadBrochureLP,
  ModalTypes.ConnectModalLP,
  ModalTypes.ConnectModal30Sec,
  ModalTypes.LoginModal,
  ModalTypes.ShareIcon,
];

export function warmHotModalChunks(): void {
  HOT_MODAL_TYPES.forEach((type) => {
    void ensureModalChunk(type).catch(() => {});
  });
}
